/** Preferências de acessibilidade do aluno (US-03), com espelho local.
 *
 * Por que `localStorage` e não só React Query: as preferências têm de valer
 * **antes do primeiro paint**. Esperar o `GET /reader/preferences` faz quem
 * escolheu tema escuro levar um flash de tela branca em toda abertura — que é
 * exatamente o incômodo que a pessoa desligou o tema claro para evitar. O
 * espelho local pinta na hora; o servidor chega depois e resolve por
 * last-write-wins (N19).
 *
 * O `localStorage` aqui é CONVENIÊNCIA, não a verdade: pode vir vazio (modo
 * privado, storage bloqueado) e pode estar velho (o aluno mudou o tema no
 * celular). A verdade é o servidor, e é por isso que o carimbo viaja junto.
 */
import { create } from 'zustand'

import {
  PADROES,
  aplicarPreferencias,
  remotoEhMaisNovo,
  sanearPreferencias,
} from '@/features/reader/utils/preferences'
import type { ReaderPreferences, ReaderPreferencesResponse } from '@/types/api'

const CHAVE = 'lumina.preferencias'

interface Espelho {
  preferencias: ReaderPreferences
  carimbo: string | null
  invertManual: boolean
}

function lerEspelho(): Espelho {
  try {
    const bruto = localStorage.getItem(CHAVE)
    if (!bruto) return { preferencias: { ...PADROES }, carimbo: null, invertManual: false }
    const objeto = JSON.parse(bruto) as Record<string, unknown>
    return {
      preferencias: sanearPreferencias(objeto.preferencias),
      carimbo: typeof objeto.carimbo === 'string' ? objeto.carimbo : null,
      invertManual: objeto.invertManual === true,
    }
  } catch {
    // JSON corrompido ou storage bloqueado: os padrões servem, e um leitor que
    // abre com o tema de fábrica é melhor que um leitor que não abre.
    return { preferencias: { ...PADROES }, carimbo: null, invertManual: false }
  }
}

function gravarEspelho(espelho: Espelho): void {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(espelho))
  } catch {
    /* modo privado: só não sobrevive ao reload */
  }
}

function pintar(preferencias: ReaderPreferences): void {
  if (typeof document === 'undefined') return
  aplicarPreferencias(preferencias, document.documentElement)
}

interface AccessibilityState {
  preferencias: ReaderPreferences
  /** Carimbo do cliente da última alteração local — ordena o LWW (N19). */
  carimbo: string | null
  /** O aluno mexeu na inversão à mão? Então o tema para de decidir por ele. */
  invertManual: boolean
  /** Há alteração local ainda não confirmada pelo servidor. */
  pendente: boolean

  definir: <C extends keyof ReaderPreferences>(campo: C, valor: ReaderPreferences[C]) => void
  restaurarPadroes: () => void
  hidratarDoServidor: (resposta: ReaderPreferencesResponse) => void
  marcarSalvo: () => void
}

const inicial = lerEspelho()

export const useAccessibilityStore = create<AccessibilityState>((set, get) => ({
  preferencias: inicial.preferencias,
  carimbo: inicial.carimbo,
  invertManual: inicial.invertManual,
  pendente: false,

  definir: (campo, valor) => {
    const estado = get()
    const preferencias: ReaderPreferences = { ...estado.preferencias, [campo]: valor }

    // O tema escuro liga a inversão da página por padrão — quem o escolheu não
    // quer uma folha branca no escuro. Mas só até o aluno decidir por si: a
    // partir daí a caixa manda, senão desmarcar e trocar de tema remarcaria.
    let invertManual = estado.invertManual
    if (campo === 'invert_pdf') {
      invertManual = true
    } else if (campo === 'theme' && !invertManual) {
      preferencias.invert_pdf = valor === 'dark'
    }

    const carimbo = new Date().toISOString()
    pintar(preferencias)
    gravarEspelho({ preferencias, carimbo, invertManual })
    set({ preferencias, carimbo, invertManual, pendente: true })
  },

  restaurarPadroes: () => {
    const preferencias = { ...PADROES }
    const carimbo = new Date().toISOString()
    pintar(preferencias)
    gravarEspelho({ preferencias, carimbo, invertManual: false })
    set({ preferencias, carimbo, invertManual: false, pendente: true })
  },

  hidratarDoServidor: (resposta) => {
    const estado = get()
    // Alteração local ainda não enviada NUNCA é sobrescrita pelo servidor: quem
    // acabou de mexer no controle está olhando para ele.
    if (estado.pendente) return
    if (!remotoEhMaisNovo(resposta.client_updated_at ?? resposta.updated_at, estado.carimbo)) {
      return
    }
    const preferencias = sanearPreferencias(resposta)
    const carimbo = resposta.client_updated_at ?? resposta.updated_at
    pintar(preferencias)
    gravarEspelho({ preferencias, carimbo, invertManual: estado.invertManual })
    set({ preferencias, carimbo, pendente: false })
  },

  marcarSalvo: () => set({ pendente: false }),
}))

/** Pinta as preferências guardadas ANTES do primeiro render (ver o topo). */
export function aplicarPreferenciasIniciais(): void {
  pintar(useAccessibilityStore.getState().preferencias)
}

/** Só para teste: devolve o store ao estado de fábrica e limpa o espelho. */
export function resetarPreferencias(): void {
  try {
    localStorage.removeItem(CHAVE)
  } catch {
    /* idem */
  }
  useAccessibilityStore.setState({
    preferencias: { ...PADROES },
    carimbo: null,
    invertManual: false,
    pendente: false,
  })
}
